import { lazy, Suspense, useState } from "react";
import { ExternalLink, ImageOff, Maximize2 } from "lucide-react";
import type { SchemaRiferimento } from "@/lib/schemaImage";
import { fonteSchema } from "@/lib/schemiFonte";
import { apriEsterno } from "@/lib/apriEsterno";

const VisoreSchema = lazy(() => import("./VisoreSchema"));

/**
 * Anteprima dello schema di cablaggio citato nella risposta.
 *
 * In cantiere lo schema è il motivo per cui si apre l'app: mostrarlo subito evita
 * il giro "leggo la citazione → cerco il manuale → trovo la pagina".
 * Se l'immagine non c'è degrada in silenzio a un riferimento testuale.
 *
 * L'ANTEPRIMA È NOSTRA, IL CLICK È DEL COSTRUTTORE (Davide, 02/09/2026):
 * «se clicco sullo schema mi deve portare alla pagina del costruttore, senza
 * uscire dall'app». Per Notifier e Ajax, che pubblicano i manuali, il tocco
 * apre la loro pagina nel browser in-app (nativo) o in una scheda (PWA):
 * i costruttori vietano di incorniciare le loro pagine dentro altri siti
 * (X-Frame-Options: SAMEORIGIN), quindi di più non si può. Per INIM e Daitem,
 * che non pubblicano, resta il nostro visore con la fonte scritta sotto.
 *
 * LA FONTE STA SCRITTA SOTTO, SEMPRE (26/08/2026): l'immagine è la pagina di
 * un manuale che non è nostro; dire da dove viene e di chi è, è la differenza
 * fra citare e appropriarsi (art. 70 l.d.a.).
 */
export function SchemaPreview({
  schema,
  titolare,
}: {
  schema: SchemaRiferimento;
  titolare?: string;
}) {
  const [errore, setErrore] = useState(false);
  const [visore, setVisore] = useState(false);
  const fonte = fonteSchema(schema);

  const etichetta = `${schema.modello} — ${schema.tipo} (pag. ${schema.pagina})`;
  const apri = () => {
    if (fonte) void apriEsterno(fonte.href);
    else setVisore(true);
  };
  const testoApri = fonte ? `Apri sul sito ${fonte.costruttore}` : "Ingrandisci";

  if (errore) {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
        <ImageOff className="h-3.5 w-3.5" />
        Schema {etichetta} — immagine non disponibile
        {fonte ? (
          <button type="button" onClick={apri} className="underline hover:text-foreground">
            {testoApri}
          </button>
        ) : null}
      </span>
    );
  }

  return (
    <figure className="mt-1.5 overflow-hidden rounded-xl border border-border/60 bg-surface/40">
      <button
        type="button"
        onClick={apri}
        className="block w-full cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        aria-label={fonte ? `${testoApri}: schema ${etichetta}` : `Ingrandisci lo schema ${etichetta}`}
      >
        <img
          src={schema.url}
          alt={`Schema di cablaggio ${etichetta}`}
          loading="lazy"
          onError={() => setErrore(true)}
          className="max-h-80 w-full bg-white object-contain"
        />
      </button>
      <figcaption className="px-3 py-2 text-xs text-muted-foreground">
        <span className="flex items-center justify-between gap-2">
          <span className="truncate">{etichetta}</span>
          <button
            type="button"
            onClick={apri}
            className="inline-flex shrink-0 items-center gap-1 hover:text-foreground"
          >
            {testoApri}
            {fonte ? <ExternalLink className="h-3 w-3" /> : <Maximize2 className="h-3 w-3" />}
          </button>
        </span>
        <span className="mt-1 block text-[11px] leading-snug opacity-80">
          Fonte: manuale {schema.modello}, pag. {schema.pagina}
          {titolare ? ` — © ${titolare}` : fonte ? ` — © ${fonte.costruttore}` : ""}.
          {fonte
            ? " Il tocco apre la pagina ufficiale del costruttore."
            : " Riprodotto per la consultazione tecnica; ogni diritto resta del costruttore."}
        </span>
      </figcaption>
      {visore ? (
        <Suspense fallback={null}>
          <VisoreSchema src={schema.url} onClose={() => setVisore(false)} />
        </Suspense>
      ) : null}
    </figure>
  );
}
